import { getPermissionsFromRoles } from '@react-admin/ra-rbac';
import { AuthProvider } from 'react-admin';

import { ZodOtpChannel } from '@Common/types/auth/otp_channel';
import type { BOExternalAdminUser } from '@Common/types/bo_external/auth/admin_user';
import {
  BOExternalUserIdentity,
  ZodBOExternalTwoFactorSignInData,
  ZodBOExternalUserIdentity,
} from '@Common/types/bo_external/auth/identity';
import { SuccessResponse } from '@Common/types/response';

import { buildEndpoint } from '@sh4res/bo-common/utils/build_endpoint';
import { BoExternalIdentityI } from '@sh4res/bo-common/utils/identity';

import { setDefaultAdminActive } from '@Components/LoginPageForm';
import { logAction } from '@Utils/analytics';
import { authClient, getAuthSession, publicAuthClient } from '@Utils/authClient';
import { maintenanceStore } from '@Utils/maintenanceStore';
import { httpClient } from '@Utils/providers/dataProvider';
import { STORAGE_KEYS } from '@Utils/storage_keys';
import { TwoFactorRequired } from '@Utils/twoFactorRequired';

export type { BoExternalIdentityI };

// Cache the permissions and roles requests
let cachedReq: BoExternalIdentityI | undefined;
let cacheExpiresAt: number | undefined;
let isLoggedIn = false;

/* oxlint-disable no-console */
/* eslint-disable no-console */
const logPermissionsAndRoles = (data: BOExternalUserIdentity) => {
  console.log('User roles:', data.user.roles);
  console.log('Role definitions:', data.role_definitions);
};
/* eslint-enable no-console */
/* oxlint-enable no-console */

const hasCacheExpired = (): boolean => {
  if (cacheExpiresAt !== undefined && Date.now() > cacheExpiresAt) {
    return true;
  }

  return false;
};

async function fetchIdentity() {
  const res = await httpClient(buildEndpoint('auth/identity', 'list'));
  const data = ZodBOExternalUserIdentity.parse(res.json);

  logPermissionsAndRoles(data);

  return data;
}

export const authProvider: AuthProvider = {
  // authentication
  login: async ({ email, password, rememberMe }) => {
    localStorage.removeItem(STORAGE_KEYS.activeAdminId);
    localStorage.removeItem(STORAGE_KEYS.adminsList);

    const { error, data } = await authClient.signIn.email({
      email,
      password,
      rememberMe,
      callbackURL: `${window.location.origin}/#/auth-callback?type=verify-email`,
    });
    if (data) {
      const twoFactorData = ZodBOExternalTwoFactorSignInData.safeParse(data);
      if (twoFactorData.success && twoFactorData.data.twoFactorRedirect) {
        logAction('Login requires two factor');

        const { data: otpChannel, error: otpChannelError } =
          await authClient.twoFactor.otpChannel();
        if (otpChannelError) {
          throw Object.assign(new Error(otpChannelError.message), otpChannelError);
        }

        throw new TwoFactorRequired(email, rememberMe, ZodOtpChannel.parse(otpChannel));
      }

      logAction('Login succeeded');
      await setDefaultAdminActive();
      return;
    }

    if (error.code === 'EMAIL_NOT_VERIFIED') {
      logAction('Login blocked, email not verified', null, 'warn');
      return {
        redirectTo: '/verify_email',
      };
    }

    // error.code and status only — never the email or password
    logAction('Login failed', { code: error.code, status: error.status }, 'warn');

    throw Object.assign(new Error(error.message), error);
  },
  checkError: async (error) => {
    const { status } = error;

    if (status === 555) {
      maintenanceStore.activate();
      return Promise.resolve();
    }
    // Since getIdentity is using dataProvider we should ignore any errors until we know we have logged in
    // Otherwise we will experience random redirects back to login page
    if (!isLoggedIn) {
      return Promise.resolve();
    }

    if (status === 401) {
      logAction('Session expired, redirecting to login', null, 'warn');
      isLoggedIn = false;
      return Promise.reject(new Error('ra.auth.auth_check_error'));
    }

    return Promise.resolve();
  },
  checkAuth: async () => {
    // already logged in, no need to check again
    if (isLoggedIn) {
      return;
    }

    const { data, error } = await getAuthSession();
    if (error) {
      throw Object.assign(new Error(error.message), error);
    }

    if (!data) {
      // {message:false} is the way to silence error notification in react-admin
      // We can't use an Error object here as message would always be string
      // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
      return Promise.reject({ message: false });
    }

    isLoggedIn = true;
  },
  logout: async () => {
    if (!isLoggedIn) {
      return;
    }
    logAction('Logout');
    isLoggedIn = false;
    cacheExpiresAt = undefined;
    cachedReq = undefined;
    await authClient.signOut();

    const restoreLocation = sessionStorage.getItem(STORAGE_KEYS.preImpersonationNavState);
    if (!restoreLocation) {
      return;
    }
    sessionStorage.removeItem(STORAGE_KEYS.preImpersonationNavState);
    return restoreLocation;
  },
  resetCache: () => {
    isLoggedIn = false;
    cacheExpiresAt = undefined;
    cachedReq = undefined;
  },

  getIdentity: async () => {
    // identity fetching would fail if we're not logged in
    if (!isLoggedIn) {
      // this error is caught and handled properly
      throw new Error('Not logged in');
    }

    if (cachedReq === undefined || hasCacheExpired()) {
      // Request and cache permissions and roles
      const data = await fetchIdentity();

      const fullName = getFullName(data.user);

      cachedReq = {
        id: data.user.id,
        email: data.user.email,
        fullName,
        companyId: data.user.company_id,
        auth_role: data.user.auth_role,
        phone_number: data.user.phone_number,
        roles: data.user.roles,
        role_definitions: data.role_definitions,
        contract_availability: data.contract_availability,
        companies: data.companies,
      };

      cacheExpiresAt = Date.now() + 1000 * 60 * 15; // 15 minutes
    }

    return cachedReq;
  },

  // authorization
  getPermissions: async () => {
    // If cache expires refresh it
    if ((hasCacheExpired() || !cachedReq) && authProvider.getIdentity) {
      // get session before to avoid short circuit of getIdentity
      if (!isLoggedIn) {
        await authProvider.checkAuth({});
      }
      await authProvider.getIdentity();
    }

    if (!cachedReq) {
      return [];
    }

    const { roles, role_definitions } = cachedReq;

    return getPermissionsFromRoles({
      roleDefinitions: role_definitions,
      userRoles: roles,
    });
  },

  getRoles: async () => {
    if ((hasCacheExpired() || !cachedReq) && authProvider.getIdentity) {
      await authProvider.getIdentity();
    }

    return cachedReq?.role_definitions ?? { Default: [] };
  },

  activation: async (params: { email: string; code?: string | null }) => {
    const {
      data: { success },
    } = await publicAuthClient.post<SuccessResponse>('/auth/activation', params);
    return success;
  },
};

function getFullName({ first_name, last_name, email }: BOExternalAdminUser) {
  return first_name && last_name ? `${first_name || ''} ${last_name || ''}` : email.split('@')[0];
}
